import { Organization } from '@solid/shared';
import { acceptOrgInvite } from '@solid/shared/services/organization.service';
import { Button, Notification } from '@solid/shared/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useContext, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/auth.context';
import { getManagementURLs, openPaddlePopup, paddleEvent } from '../../services/payment.service';
import * as Styled from './OrgAcceptInviteForm.styles';
import { Trans, useTranslation } from 'react-i18next';

interface OrgAcceptInviteFormProps {
  organization: Organization;
  token: string;
}

/**
 * This component is used to accept an invitation to an organization, if the user already has an account.
 */
const OrgAcceptInviteForm: React.FC<OrgAcceptInviteFormProps> = ({ organization, token }) => {
  const { refetchAuthState } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    data: managementUrls,
    isLoading: isLoadingUrls,
    refetch: refetchUrls,
  } = useQuery(['management-urls'], () => getManagementURLs(), {
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    const sub = paddleEvent.subscribe(data => {
      // Automatically refetch management urls upon cancellation
      console.log(data.event);
      if (data.event === 'Checkout.Complete') {
        refetchUrls();
      }
    });
    return () => sub.unsubscribe();
  }, [refetchUrls]);

  const {
    mutate: acceptInvite,
    error,
    isLoading: isLoadingAccept,
    isSuccess: isSuccessAccept,
  } = useMutation<void, Error, void>(['accept-org-invite', token], async () => {
    const promise = acceptOrgInvite(token);

    toast.promise(promise, {
      loading: t('pages.orgInvite.acceptInviteForm.invitationLoadingText'),
      success: t('pages.orgInvite.acceptInviteForm.invitationSuccessText'),
      error: t('pages.orgInvite.acceptInviteForm.invitationErrorText'),
    });

    await promise;

    refetchAuthState();
    setTimeout(() => navigate('/'), 1000); // Short delay to allow user to read success message
  });

  const isLoading = isLoadingAccept || isLoadingUrls;
  const hasActivePlan = !!managementUrls?.cancellationUrl;

  return (
    <div>
      <Styled.InfoContainer>
        <Notification state='warning' hasBrighterText>
          <Trans
            i18nKey='pages.orgInvite.acceptInviteForm.acceptInvitationWarningText'
            components={{ Link: <Link to='/checks' /> }}
          />
        </Notification>
      </Styled.InfoContainer>

      <Styled.Form
        onSubmit={e => {
          e.preventDefault();
          acceptInvite();
        }}>
        <div className='form-field'>
          <label className='checkbox' htmlFor='accept-move-checks-to-org'>
            <input type='checkbox' id='accept-move-checks-to-org' name='accept-move-checks-to-org' required />
            <span>
              {t('pages.orgInvite.acceptInviteForm.acceptInvitationCheckboxText', {
                organizationName: organization.displayName,
              })}
            </span>
          </label>
        </div>

        {hasActivePlan && (
          <Styled.InfoContainer>
            <Notification state='danger' hasBrighterText>
              {t('pages.orgInvite.acceptInviteForm.activeSubscriptionWarningText')}
            </Notification>
            <p></p>
            <Button
              themeColor='danger'
              variant='outlined'
              type='button'
              isLoading={isLoadingUrls}
              onClick={() => openPaddlePopup(managementUrls.cancellationUrl)}>
              {t('pages.orgInvite.acceptInviteForm.cancelSubscriptionButton')}
            </Button>
          </Styled.InfoContainer>
        )}

        <div className='form-field'>{error && <div className='notification is-error'>{error.message}</div>}</div>

        {hasActivePlan === false && isSuccessAccept === false && (
          <Button type='submit' themeColor='primary' isLoading={isLoading} disabled={isLoading}>
            {t('pages.orgInvite.acceptInviteForm.acceptInvitationButton')}
          </Button>
        )}
      </Styled.Form>
    </div>
  );
};

export default OrgAcceptInviteForm;
